/**
 * Black-Scholes option pricing model with robust Implied Volatility solver
 */

// Standard normal CDF
function normCDF(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * absX);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return 0.5 * (1.0 + sign * y);
}

// Standard normal PDF
function normPDF(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}


const validInputs = (S,K,T,r,sigma=0) => [S,K,T,r,sigma].every(Number.isFinite) && S>0 && K>0 && T>=0 && sigma>=0;
const validType = type => type==='call' || type==='put';
function dValues(S,K,T,r,sigma) {
  const d1=(Math.log(S/K)+(r+sigma*sigma/2)*T)/(sigma*Math.sqrt(T));
  return [d1,d1-sigma*Math.sqrt(T)];
}
export function blackScholesPrice(S,K,T,r,sigma,type) {
  if (!validInputs(S,K,T,r,sigma) || !validType(type)) return NaN;
  if (T===0) return calculateIntrinsicValue(S,K,type);
  const discounted=K*Math.exp(-r*T);
  if (sigma===0) return Math.max(type==='call'?S-discounted:discounted-S,0);
  const [d1,d2]=dValues(S,K,T,r,sigma);
  return Math.max(0,type==='call'?S*normCDF(d1)-discounted*normCDF(d2):discounted*normCDF(-d2)-S*normCDF(-d1));
}
export const blackScholesCall=(S,K,T,r,sigma)=>blackScholesPrice(S,K,T,r,sigma,'call');
export const blackScholesPut=(S,K,T,r,sigma)=>blackScholesPrice(S,K,T,r,sigma,'put');
export function calculateIntrinsicValue(S,K,type) {
  return validType(type)?Math.max(type==='call'?S-K:K-S,0):NaN;
}
export const calculateTimeValue=(premium,intrinsic)=>premium-intrinsic;
export function calculateGreeks(S,K,T,r,sigma,type) {
  if (!validInputs(S,K,T,r,sigma) || !validType(type)) return null;
  const discounted=K*Math.exp(-r*T);
  if (T===0 || sigma===0) {
    const forwardITM=type==='call'?S>discounted:S<discounted;
    const atBoundary=S===discounted;
    const delta=(forwardITM?1:atBoundary?0.5:0)*(type==='call'?1:-1);
    return {delta,gamma:0,vega:0,theta:T && forwardITM?(type==='call'?-1:1)*r*discounted/365:0,rho:T*discounted*delta/100};
  }
  const [d1,d2]=dValues(S,K,T,r,sigma), density=normPDF(d1), root=Math.sqrt(T);
  return {
    delta:type==='call'?normCDF(d1):normCDF(d1)-1,
    gamma:density/(S*sigma*root),
    vega:S*density*root/100, // Rial per one percentage POINT of volatility.
    theta:(-S*density*sigma/(2*root)+(type==='call'?-r*discounted*normCDF(d2):r*discounted*normCDF(-d2)))/365,
    rho:(type==='call'?discounted*T*normCDF(d2):-discounted*T*normCDF(-d2))/100
  };
}
export const calculateCallGreeks=(S,K,T,r,sigma)=>calculateGreeks(S,K,T,r,sigma,'call');
export const calculatePutGreeks=(S,K,T,r,sigma)=>calculateGreeks(S,K,T,r,sigma,'put');
function jalaliToGregorian(jy, jm, jd) {
  jy = parseInt(jy);
  jm = parseInt(jm);
  jd = parseInt(jd);

  let gy = jy <= 979 ? 621 : 1600;
  jy -= jy <= 979 ? 0 : 979;

  let days = (jy <= 979 ? 0 : 365 * 33) +
    365 * jy +
    Math.floor(jy / 33) * 8 +
    Math.floor(((jy % 33) + 3) / 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);

  gy += 400 * Math.floor(days / 146097);
  days %= 146097;

  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }

  gy += 4 * Math.floor(days / 1461);
  days %= 1461;

  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }

  let gd = days + 1;
  const gMonthDays = [0, 31, (gy % 4 === 0 && (gy % 100 !== 0 || gy % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let gm = 0;
  for (let i = 1; i <= 12; i++) {
    if (gd <= gMonthDays[i]) {
      gm = i;
      break;
    }
    gd -= gMonthDays[i];
  }

  return [gy, gm - 1, gd];
}

function formatGregorianDate(gy, gm, gd) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${gy}/${pad(gm + 1)}/${pad(gd)}`;
}


const DAY=86400000;
function latin(value) {
  return String(value).replace(/[۰-۹]/g,c=>String('۰۱۲۳۴۵۶۷۸۹'.indexOf(c))).replace(/[٠-٩]/g,c=>String('٠١٢٣٤٥٦٧٨٩'.indexOf(c)));
}
export function parseOptionDate(value) {
  const match=latin(value||'').trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (!match) return null;
  const [y,m,d]=match.slice(1).map(Number);
  if (m<1 || m>12 || d<1 || d>31) return null;
  const jalali=y>=1300 && y<=1599;
  if (!jalali && (y<1900 || y>2200)) return null;
  const [gy,gm,gd]=jalali?jalaliToGregorian(y,m,d):[y,m-1,d];
  const result=new Date(Date.UTC(gy,gm,gd));
  if (!Number.isFinite(result.getTime())) return null;
  if (jalali) {
    const parts=new Intl.DateTimeFormat('en-US-u-ca-persian',{timeZone:'UTC',year:'numeric',month:'numeric',day:'numeric'}).formatToParts(result);
    const values=Object.fromEntries(parts.map(p=>[p.type,p.value]));
    if (+values.year!==y || +values.month!==m || +values.day!==d) return null;
  } else if (result.getUTCFullYear()!==y || result.getUTCMonth()!==m-1 || result.getUTCDate()!==d) return null;
  return result;
}
function tehranDay(now) {
  if (typeof now==='string') return parseOptionDate(now);
  if (!(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
  const parts=new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Tehran',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(now);
  const values=Object.fromEntries(parts.map(p=>[p.type,p.value]));
  return new Date(Date.UTC(+values.year,+values.month-1,+values.day));
}
export function calculateTimeToMaturity(maturityDate,asOf=new Date()) {
  const maturity=parseOptionDate(maturityDate), today=tehranDay(asOf);
  return maturity && today?Math.max(0,(maturity-today)/DAY)/365:NaN;
}
export function calculateImpliedVolatility(S,K,T,r,premium,type,initialGuess=0.4,tolerance=0.0001,maxIterations=200) {
  if (!validInputs(S,K,T,r) || T===0 || !validType(type) || !Number.isFinite(premium) || premium<0) return null;
  const lower=blackScholesPrice(S,K,T,r,0,type),upper=type==='call'?S:K*Math.exp(-r*T);
  const epsilon=Math.max(1e-8,Math.max(S,K)*1e-10);
  if (premium<lower-epsilon || premium>=upper) return null;
  if (Math.abs(premium-lower)<=epsilon) return 0;
  let low=0,high=Math.max(0.5,Number.isFinite(initialGuess)?initialGuess:0.4);
  while (high<32 && blackScholesPrice(S,K,T,r,high,type)<premium) high*=2;
  if (blackScholesPrice(S,K,T,r,high,type)<premium) return null;
  for(let i=0;i<Math.max(100,maxIterations);i++) {
    const mid=(low+high)/2,price=blackScholesPrice(S,K,T,r,mid,type);
    if (Math.abs(price-premium)<=tolerance) return mid;
    if(price<premium) low=mid; else high=mid;
  }
  const result=(low+high)/2;
  return Math.abs(blackScholesPrice(S,K,T,r,result,type)-premium)<=tolerance?result:null;
}
// Vega accepts percentage points (e.g. 2 means IV rising from 30% to 32%).
export function predictOptionPremium(premium,delta,gamma,vega,theta,deltaS,deltaIV,elapsedDays=1) {
  if (![premium,delta,gamma,vega,theta,deltaS,deltaIV,elapsedDays].every(Number.isFinite) || elapsedDays<0) return null;
  return Math.max(0,premium+delta*deltaS+0.5*gamma*deltaS*deltaS+vega*deltaIV+theta*elapsedDays);
}
/** European Black-Scholes, no dividend yield, continuously compounded annual rate.
 * Reference price holds the prior observation's IV fixed; it is not a forecast.
 * Previous observation defaults to one calendar day earlier; caller may supply its date.
 */
export function calculateOptionValueFromAPI(symbol,S,premium,K,maturityDate,baseSymbol,type,previousPremium=0,previousS=0,initialGuess=0.4,r=0.3,actualDeltaS=null,context={}) {
  const asOf=tehranDay(context.asOf || new Date()),maturity=parseOptionDate(maturityDate);
  const valid=validInputs(S,K,0,r) && Number.isFinite(premium) && premium>=0 && validType(type) && !!asOf && !!maturity;
  if (!valid) return {isValid:false,ivSolved:false,reason:'قیمت، نوع قرارداد یا تاریخ سررسید نامعتبر است',predictions:{predictedTomorrowPrice:'N/A'}};
  const days=Math.max(0,(maturity-asOf)/DAY), T=days/365;
  const previousDate=context.previousDate?parseOptionDate(context.previousDate):new Date(asOf.getTime()-DAY);
  const elapsed=previousDate?(asOf-previousDate)/DAY:NaN;
  const previousT=previousDate?Math.max(0,(maturity-previousDate)/DAY)/365:NaN;
  const intrinsic=calculateIntrinsicValue(S,K,type), timeValue=calculateTimeValue(premium,intrinsic);
  const iv=calculateImpliedVolatility(S,K,T,r,premium,type,initialGuess);
  const previousIV=previousS>0 && previousPremium>0 && elapsed>0?calculateImpliedVolatility(previousS,K,previousT,r,previousPremium,type,initialGuess):null;
  const difference=iv!==null && previousIV!==null?iv-previousIV:null;
  const greeks=iv!==null?calculateGreeks(S,K,T,r,iv,type):null;
  const reference=previousIV!==null && days>0?blackScholesPrice(S,K,T,r,previousIV,type):null;
  const deltaS=previousS>0?S-previousS:null;
  const formatted=value=>value===null?'N/A':value.toFixed(4);
  return {
    isValid:true,ivSolved:iv!==null,symbol,baseSymbol,optionType:type,strikePrice:K,maturityDate,
    maturityDateGregorian:formatGregorianDate(maturity.getUTCFullYear(),maturity.getUTCMonth(),maturity.getUTCDate()),
    timeToMaturity:T.toFixed(8),daysToMaturity:days,underlyingPrice:S,optionPrice:premium,previousPremium,previousUnderlyingPrice:previousS,
    intrinsicValue:intrinsic.toFixed(2),timeValue:timeValue.toFixed(2),fairValue:reference===null?'N/A':reference.toFixed(4),
    riskFreeRate:r.toFixed(4),initialGuess:initialGuess.toFixed(4),
    ivToday:iv===null?null:iv.toFixed(8),ivTodayPercent:iv===null?'N/A':(iv*100).toFixed(2),
    ivYesterday:previousIV===null?null:previousIV.toFixed(8),ivYesterdayPercent:previousIV===null?'N/A':(previousIV*100).toFixed(2),
    deltaIV:formatted(difference),deltaIVPercent:difference===null?'N/A':(difference*100).toFixed(2),
    greeks:greeks?Object.fromEntries(Object.entries(greeks).map(([key,value])=>[key,value.toFixed(key==='gamma'?10:6)])):null,
    expired:days===0,previousDateAssumed:!context.previousDate,previousDate:previousDate?previousDate.toISOString().slice(0,10):null,elapsedDays:elapsed,
    reason:days===0?'سررسید رسیده یا گذشته است؛ IV و برآورد قیمت ارائه نمی‌شود':iv===null?'قیمت با کران‌های مدل سازگار نیست؛ IV قابل حل نیست':null,
    referenceReason:reference===null?'قیمت و تاریخ معتبر مشاهده قبلی و سهم پایه برای محاسبه قیمت مبنا لازم است':null,
    predictions:{actualDeltaS:deltaS,actualDeltaIV:difference,actualDeltaIVDecimal:difference,actualDeltaIVPoints:difference===null?null:difference*100,
      predictedTomorrowPrice:reference===null?'N/A':reference.toFixed(2),referencePrice:reference,
      method:'Black-Scholes با IV ثابت مشاهده قبلی',underlyingPriceToday:S,previousUnderlyingPrice:previousS}
  };
}
